import { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import type { DateSelectArg, EventClickArg, DatesSetArg, EventContentArg } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import { format } from 'date-fns';
import { calendarApi, propertyApi, type CalendarEvent, type CalendarDayDisplay } from '@/lib/api';
import { getHolidayBackgroundEvents } from '@/lib/holidays';
import { useAuth } from '@/context/AuthContext';
import BookingModal from './BookingModal';
import CalendarLegend from './CalendarLegend';
import EventDetailModal from './EventDetailModal';

type ViewType = 'dayGridMonth' | 'listWeek';

type ExpandedEvent = {
  id: string;
  title: string;
  start: string;
  end: string;
  display: string;
  backgroundColor: string;
  borderColor: string;
  textColor?: string;
  classNames: string[];
  extendedProps: CalendarEvent['extendedProps'] & { originalEvent: CalendarEvent };
};

// Expand multi-day events into individual day events for proper per-day triangle rendering
// Also generates label events for owner/admin to see names
function expandEventsIntoDays(events: CalendarEvent[], userRole: string): ExpandedEvent[] {
  const expandedEvents: ExpandedEvent[] = [];
  const isPrivileged = userRole === 'owner' || userRole === 'admin';

  for (const event of events) {
    const startDate = new Date(event.start);
    const endDate = new Date(event.end);

    // Calculate number of days (end is exclusive in FullCalendar)
    const dayCount = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    if (dayCount <= 1) {
      // Single day event - background
      expandedEvents.push({
        id: `${event.id}-day-0`,
        title: '',
        start: event.start,
        end: event.end,
        display: 'background',
        backgroundColor: event.backgroundColor,
        borderColor: event.borderColor,
        classNames: ['booking-day', 'booking-single', ...event.classNames],
        extendedProps: { ...event.extendedProps, originalEvent: event },
      });
    } else {
      // Multi-day event - create one background event per day
      for (let i = 0; i < dayCount; i++) {
        const dayDate = new Date(startDate);
        dayDate.setDate(dayDate.getDate() + i);
        const dayStr = dayDate.toISOString().split('T')[0];

        const nextDay = new Date(dayDate);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDayStr = nextDay.toISOString().split('T')[0];

        let positionClass = 'booking-middle';
        if (i === 0) {
          positionClass = 'booking-start';
        } else if (i === dayCount - 1) {
          positionClass = 'booking-end';
        }

        expandedEvents.push({
          id: `${event.id}-day-${i}`,
          title: '',
          start: dayStr,
          end: nextDayStr,
          display: 'background',
          backgroundColor: event.backgroundColor,
          borderColor: event.borderColor,
          classNames: ['booking-day', positionClass, ...event.classNames],
          extendedProps: { ...event.extendedProps, originalEvent: event },
        });
      }
    }

    // Add a label event for the booking (shows name for owner/admin, or own bookings)
    // Label spans all FULL days (excludes start triangle and end triangle)
    const showDetails = isPrivileged || event.extendedProps.isOwnBooking;
    const labelTitle = showDetails ? event.title : 'Reserved';

    // Single night stay (2 calendar days): show label over both triangle days
    // Multi-night stays (3+ calendar days): span label across full days only
    if (dayCount === 2) {
      // Single night stay - arrive day 1 noon, depart day 2 noon
      // Show label spanning both days (over the triangles)
      expandedEvents.push({
        id: `${event.id}-label`,
        title: labelTitle,
        start: event.start,
        end: event.end,
        display: 'block',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderColor: 'transparent',
        textColor: '#ffffff',
        classNames: ['booking-label', ...event.classNames],
        extendedProps: { ...event.extendedProps, originalEvent: event },
      });
    } else if (dayCount > 2) {
      // Multi-night stay - span label across all full days (day 2 to day n-1)
      const labelStartDate = new Date(startDate);
      labelStartDate.setDate(labelStartDate.getDate() + 1);
      const labelStart = labelStartDate.toISOString().split('T')[0];

      const labelEndDate = new Date(endDate);
      labelEndDate.setDate(labelEndDate.getDate() - 1);
      const labelEnd = labelEndDate.toISOString().split('T')[0];

      expandedEvents.push({
        id: `${event.id}-label`,
        title: labelTitle,
        start: labelStart,
        end: labelEnd,
        display: 'block',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderColor: 'transparent',
        textColor: '#ffffff',
        classNames: ['booking-label', ...event.classNames],
        extendedProps: { ...event.extendedProps, originalEvent: event },
      });
    }
  }

  return expandedEvents;
}

export interface CalendarHandle {
  goToDate: (date: string) => void;
}

const Calendar = forwardRef<CalendarHandle>(function Calendar(_, ref) {
  const { user } = useAuth();
  const calendarRef = useRef<FullCalendar>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [holidayEvents, setHolidayEvents] = useState<ReturnType<typeof getHolidayBackgroundEvents>>([]);
  const [userColor, setUserColor] = useState('#3B82F6');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<ViewType>('dayGridMonth');
  const [dayDisplay, setDayDisplay] = useState<CalendarDayDisplay>('half-bar');

  // Modal states
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<{ start: Date; end: Date } | null>(null);
  const [clickSelectionStart, setClickSelectionStart] = useState<Date | null>(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  // Current date range for fetching
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);

  const fetchEvents = useCallback(async (start: string, end: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await calendarApi.getEvents(start, end);
      setEvents(response.events);
      setUserColor(response.userColor);
    } catch (err) {
      setError('Failed to load calendar events');
      console.error('Calendar fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch events when date range changes
  useEffect(() => {
    if (dateRange) {
      fetchEvents(dateRange.start, dateRange.end);
    }
  }, [dateRange, fetchEvents]);

  // Fetch property settings for display mode
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { property } = await propertyApi.get();
        if (property.calendar_day_display) {
          setDayDisplay(property.calendar_day_display);
        }
      } catch (err) {
        // Ignore errors, use default display mode
        console.error('Failed to fetch property settings:', err);
      }
    };
    fetchSettings();
  }, []);

  const handleDatesSet = useCallback((arg: DatesSetArg) => {
    const start = arg.start.toISOString().split('T')[0];
    const end = arg.end.toISOString().split('T')[0];
    setDateRange({ start, end });
    // Update holiday events for the new date range
    setHolidayEvents(getHolidayBackgroundEvents(start, end));
  }, []);

  const handleDateSelect = useCallback((selectInfo: DateSelectArg) => {
    setClickSelectionStart(null);
    // Adjust end date - FullCalendar uses exclusive end dates
    const endDate = new Date(selectInfo.end);
    endDate.setDate(endDate.getDate() - 1);

    // Ensure at least 1 night. A single-day selection would otherwise become 0 nights.
    if (endDate.getTime() <= selectInfo.start.getTime()) {
      endDate.setTime(selectInfo.start.getTime());
      endDate.setDate(endDate.getDate() + 1);
    }

    setSelectedRange({
      start: selectInfo.start,
      end: endDate,
    });
    setBookingModalOpen(true);

    // Clear the selection
    const calendarApi = selectInfo.view.calendar;
    calendarApi.unselect();
  }, []);

  // Expand events into individual days for proper per-day rendering
  const expandedEvents = useMemo(
    () => expandEventsIntoDays(events, user?.role || 'user'),
    [events, user?.role]
  );

  const handleEventClick = useCallback((clickInfo: EventClickArg) => {
    // Ignore holiday background events
    if (clickInfo.event.extendedProps?.isHoliday) {
      return;
    }

    // When selecting a date range (check-in set, waiting for checkout),
    // treat event clicks as date selections instead of opening detail
    if (clickSelectionStart) {
      const clickedDate = clickInfo.event.start;
      if (clickedDate) {
        const clicked = new Date(clickedDate);
        clicked.setHours(0, 0, 0, 0);
        const start = new Date(clickSelectionStart);
        start.setHours(0, 0, 0, 0);

        if (clicked.getTime() <= start.getTime()) {
          setClickSelectionStart(clicked);
        } else {
          setSelectedRange({ start, end: clicked });
          setBookingModalOpen(true);
          setClickSelectionStart(null);
        }
        return;
      }
    }

    // Get the original event from extendedProps (for expanded day events)
    const originalEvent = clickInfo.event.extendedProps.originalEvent as CalendarEvent | undefined;
    if (originalEvent) {
      setSelectedEvent(originalEvent);
      setEventDetailOpen(true);
    } else {
      // Fallback for non-expanded events
      const event = events.find(e => e.id === clickInfo.event.id);
      if (event) {
        setSelectedEvent(event);
        setEventDetailOpen(true);
      }
    }
  }, [events, clickSelectionStart]);

  // Handle single date click (for mobile Safari tap support)
  const handleDateClick = useCallback((info: DateClickArg) => {
    if (bookingModalOpen) return;

    // Two-tap selection: first tap sets check-in, second tap sets checkout.
    // This avoids opening the modal immediately, which makes "nights" appear stuck at 1 on mobile.
    const clicked = new Date(info.date);
    clicked.setHours(0, 0, 0, 0);

    if (!clickSelectionStart) {
      setClickSelectionStart(clicked);
      return;
    }

    const start = new Date(clickSelectionStart);
    start.setHours(0, 0, 0, 0);

    // If the user taps an earlier/same date, treat it as a new check-in date.
    if (clicked.getTime() <= start.getTime()) {
      setClickSelectionStart(clicked);
      return;
    }

    setSelectedRange({ start, end: clicked });
    setBookingModalOpen(true);
    setClickSelectionStart(null);
  }, [bookingModalOpen, clickSelectionStart]);

  const handleViewChange = (view: ViewType) => {
    setCurrentView(view);
    calendarRef.current?.getApi().changeView(view);
  };

  const handleBookingComplete = () => {
    // Refresh events after booking
    if (dateRange) {
      fetchEvents(dateRange.start, dateRange.end);
    }
  };

  const handleToday = () => {
    calendarRef.current?.getApi().today();
  };

  const handlePrev = () => {
    calendarRef.current?.getApi().prev();
  };

  const handleNext = () => {
    calendarRef.current?.getApi().next();
  };

  // Expose goToDate method via ref
  useImperativeHandle(ref, () => ({
    goToDate: (date: string) => {
      calendarRef.current?.getApi().gotoDate(date);
    },
  }), []);

  const getTitle = () => {
    const api = calendarRef.current?.getApi();
    if (!api) return '';
    return api.view.title;
  };

  return (
    <div className="space-y-4" data-tour="calendar">
      {/* Calendar Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrev}
            className="p-2 hover:bg-gray-100 rounded-md"
            aria-label="Previous"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={handleToday}
            className="px-3 py-1.5 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Today
          </button>
          <button
            onClick={handleNext}
            className="p-2 hover:bg-gray-100 rounded-md"
            aria-label="Next"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold text-gray-900 ml-2">{getTitle()}</h2>
        </div>

        {/* Jump to Date - centered */}
        <div className="flex items-center gap-2">
          <label htmlFor="jump-to-date" className="text-sm font-medium text-gray-600">
            Jump to date:
          </label>
          <input
            id="jump-to-date"
            type="date"
            onChange={(e) => {
              if (e.target.value) {
                calendarRef.current?.getApi().gotoDate(e.target.value);
              }
            }}
            className="px-2 py-1 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex rounded-md shadow-sm">
            <button
              onClick={() => handleViewChange('dayGridMonth')}
              className={`px-3 py-1.5 text-sm font-medium rounded-l-md border ${
                currentView === 'dayGridMonth'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => handleViewChange('listWeek')}
              className={`px-3 py-1.5 text-sm font-medium rounded-r-md border-t border-b border-r ${
                currentView === 'listWeek'
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              List
            </button>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoading && (
        <div className="absolute top-2 right-2 z-10">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Calendar */}
      <div className={`card calendar-container display-${dayDisplay}`}>
        {clickSelectionStart && !bookingModalOpen && (
          <div className="mb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-900">
            <span>
              Check-in selected: {format(clickSelectionStart, 'MMM d, yyyy')}. Select a checkout date.
            </span>
            <button
              type="button"
              onClick={() => setClickSelectionStart(null)}
              className="font-medium text-primary-700 hover:text-primary-900"
            >
              Clear
            </button>
          </div>
        )}
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
          initialView="dayGridMonth"
          headerToolbar={false}
          events={[...expandedEvents, ...holidayEvents]}
          selectable={true}
          selectMirror={true}
          dayMaxEvents={3}
          weekends={true}
          select={handleDateSelect}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          height="auto"
          eventDisplay="block"
          displayEventTime={false}
          selectMinDistance={5}
          unselectAuto={false}
          selectOverlap={true}
          longPressDelay={100}
          selectLongPressDelay={100}
          eventClassNames={(arg: EventContentArg) => {
            return (arg.event.extendedProps.classNames as string[]) || [];
          }}
        />
      </div>

      {/* Legend */}
      <CalendarLegend userColor={userColor} userRole={user?.role || 'user'} />

      {/* Booking Modal */}
      <BookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setClickSelectionStart(null);
        }}
        selectedRange={selectedRange}
        onBookingComplete={handleBookingComplete}
      />

      {/* Event Detail Modal */}
      <EventDetailModal
        isOpen={eventDetailOpen}
        onClose={() => setEventDetailOpen(false)}
        event={selectedEvent}
        userRole={user?.role || 'user'}
        onUpdate={handleBookingComplete}
      />
    </div>
  );
});

export default Calendar;
